import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
    DialogDescription,
} from '../../components/ui/dialog';
import { 
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow 
} from '../../components/ui/table';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../../components/ui/tabs';
import { 
    Users, 
    UserPlus,
    Briefcase,
    TrendingUp, 
    Loader2,
    Trash2,
    Mail,
    Send,
    Clock,
    CheckCircle,
    RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSalesRepsPage = () => {
    const { token } = useAuth();
    const { tenant } = useTenant();
    
    const [salesReps, setSalesReps] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingInvites, setLoadingInvites] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [deleteInviteId, setDeleteInviteId] = useState(null);
    const [resendingId, setResendingId] = useState(null);
    const [formData, setFormData] = useState({ name: '', email: '' });

    useEffect(() => {
        fetchSalesReps();
        fetchInvitations();
    }, []);

    const fetchSalesReps = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API}/admin/sales-reps`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSalesReps(response.data);
        } catch (error) {
            toast.error('Vertriebler konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    const fetchInvitations = async () => {
        setLoadingInvites(true);
        try {
            const response = await axios.get(`${API}/admin/sales-invites`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setInvitations(response.data);
        } catch (error) {
            console.error('Failed to load invitations:', error);
        } finally {
            setLoadingInvites(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.email) {
            toast.error('Alle Felder sind erforderlich');
            return;
        }
        
        setSubmitting(true);
        try {
            await axios.post(
                `${API}/admin/sales-invites`,
                formData,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Einladung wurde gesendet');
            setDialogOpen(false);
            setFormData({ name: '', email: '' });
            fetchInvitations();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Einladung fehlgeschlagen');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        
        try {
            await axios.delete(`${API}/admin/sales-reps/${deleteId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Vertriebler gelöscht');
            setDeleteId(null);
            fetchSalesReps();
        } catch (error) {
            toast.error('Löschen fehlgeschlagen');
        }
    };

    const handleDeleteInvite = async () => {
        if (!deleteInviteId) return;
        
        try {
            await axios.delete(`${API}/admin/sales-invites/${deleteInviteId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Einladung gelöscht');
            setDeleteInviteId(null);
            fetchInvitations();
        } catch (error) {
            toast.error('Löschen fehlgeschlagen');
        }
    };

    const handleResendInvite = async (inviteId) => {
        setResendingId(inviteId);
        try {
            const response = await axios.post(`${API}/admin/sales-invites/${inviteId}/resend`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // Update local state with email status
            if (response.data.email_sent) {
                setInvitations(prev => prev.map(inv => 
                    inv.id === inviteId 
                        ? { ...inv, email_sent: true, email_sent_at: new Date().toISOString() }
                        : inv
                ));
                toast.success('Einladung erneut gesendet');
            } else {
                toast.warning('Einladung aktualisiert, aber E-Mail konnte nicht gesendet werden');
            }
        } catch (error) {
            toast.error('Erneutes Senden fehlgeschlagen');
        } finally {
            setResendingId(null);
        }
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('de-DE', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const pendingInvites = invitations.filter(i => i.status === 'pending');

    return (
        <DashboardLayout>
            <div className="space-y-8 animate-fade-in">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900">Vertriebler</h1>
                        <p className="text-slate-600 mt-1">Verwalten Sie Ihre Vertriebsmitarbeiter</p>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button 
                                style={{ backgroundColor: tenant?.primary_color }}
                                className="gap-2"
                                data-testid="invite-sales-rep-btn"
                            >
                                <Mail size={18} />
                                Vertriebler einladen
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Vertriebler einladen</DialogTitle>
                                <DialogDescription>
                                    Der Vertriebler erhält eine E-Mail mit einem Link, um sein Passwort festzulegen und sein Konto zu aktivieren.
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Name *</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="Max Müller"
                                        required
                                        data-testid="invite-name-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email">E-Mail *</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        placeholder="max@firma.de"
                                        required
                                        data-testid="invite-email-input"
                                    />
                                </div>
                                <DialogFooter className="pt-4">
                                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                        Abbrechen
                                    </Button>
                                    <Button 
                                        type="submit" 
                                        disabled={submitting}
                                        style={{ backgroundColor: tenant?.primary_color }}
                                        data-testid="send-invite-btn"
                                    >
                                        {submitting ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <>
                                                <Send size={16} className="mr-2" />
                                                Einladung senden
                                            </>
                                        )}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Stats Overview */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                                    <Briefcase size={24} className="text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Aktive Vertriebler</p>
                                    <p className="text-2xl font-bold text-slate-900">{salesReps.length}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
                                    <Clock size={24} className="text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Offene Einladungen</p>
                                    <p className="text-2xl font-bold text-slate-900">{pendingInvites.length}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                                    <Users size={24} className="text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Zugewiesene Kunden</p>
                                    <p className="text-2xl font-bold text-slate-900">
                                        {salesReps.reduce((sum, rep) => sum + (rep.customer_count || 0), 0)}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center">
                                    <TrendingUp size={24} className="text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Empfehlungen gesamt</p>
                                    <p className="text-2xl font-bold text-slate-900">
                                        {salesReps.reduce((sum, rep) => sum + (rep.referral_count || 0), 0)}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Tabs for Active Sales Reps and Pending Invitations */}
                <Tabs defaultValue="active" className="w-full">
                    <TabsList>
                        <TabsTrigger value="active" className="gap-2">
                            <CheckCircle size={16} />
                            Aktive Vertriebler ({salesReps.length})
                        </TabsTrigger>
                        <TabsTrigger value="pending" className="gap-2">
                            <Clock size={16} />
                            Offene Einladungen ({pendingInvites.length})
                        </TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="active">
                        <Card>
                            <CardHeader>
                                <CardTitle>Aktive Vertriebler</CardTitle>
                                <CardDescription>Übersicht aller aktiven Vertriebsmitarbeiter</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {loading ? (
                                    <div className="space-y-3">
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
                                        ))}
                                    </div>
                                ) : salesReps.length === 0 ? (
                                    <div className="text-center py-12">
                                        <Briefcase size={48} className="mx-auto text-slate-300 mb-3" />
                                        <p className="text-slate-500">Noch keine aktiven Vertriebler</p>
                                        <p className="text-sm text-slate-400 mt-1">
                                            Laden Sie Vertriebler per E-Mail ein, um loszulegen
                                        </p>
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Name</TableHead>
                                                <TableHead>E-Mail</TableHead>
                                                <TableHead>Kunden</TableHead>
                                                <TableHead>Empfehlungen</TableHead>
                                                <TableHead>Erstellt</TableHead>
                                                <TableHead className="text-right">Aktionen</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {salesReps.map((rep) => (
                                                <TableRow key={rep.id} data-testid={`sales-rep-row-${rep.id}`}>
                                                    <TableCell className="font-medium">{rep.name}</TableCell>
                                                    <TableCell>{rep.email}</TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline">{rep.customer_count || 0}</Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline">{rep.referral_count || 0}</Badge>
                                                    </TableCell>
                                                    <TableCell>{formatDate(rep.created_at)}</TableCell>
                                                    <TableCell className="text-right">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                                            onClick={() => setDeleteId(rep.id)}
                                                            data-testid={`delete-sales-rep-${rep.id}`}
                                                        >
                                                            <Trash2 size={16} />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                    
                    <TabsContent value="pending">
                        <Card>
                            <CardHeader>
                                <CardTitle>Offene Einladungen</CardTitle>
                                <CardDescription>Noch nicht akzeptierte Einladungen</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {loadingInvites ? (
                                    <div className="space-y-3">
                                        {[1, 2].map(i => (
                                            <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
                                        ))}
                                    </div>
                                ) : pendingInvites.length === 0 ? (
                                    <div className="text-center py-12">
                                        <Mail size={48} className="mx-auto text-slate-300 mb-3" />
                                        <p className="text-slate-500">Keine offenen Einladungen</p>
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Name</TableHead>
                                                <TableHead>E-Mail</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead>E-Mail versendet</TableHead>
                                                <TableHead>Gesendet am</TableHead>
                                                <TableHead>Gültig bis</TableHead>
                                                <TableHead className="text-right">Aktionen</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {pendingInvites.map((invite) => (
                                                <TableRow key={invite.id} data-testid={`invite-row-${invite.id}`}>
                                                    <TableCell className="font-medium">{invite.name}</TableCell>
                                                    <TableCell>{invite.email}</TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                                            <Clock size={12} className="mr-1" />
                                                            Ausstehend
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        {invite.email_sent ? (
                                                            <div className="flex items-center gap-1 text-green-600" title={invite.email_sent_at ? `Gesendet: ${formatDate(invite.email_sent_at)}` : 'E-Mail gesendet'}>
                                                                <CheckCircle size={16} />
                                                                <span className="text-sm">Ja</span>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-1 text-amber-600" title="E-Mail wurde nicht gesendet">
                                                                <Clock size={16} />
                                                                <span className="text-sm">Nein</span>
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>{formatDate(invite.created_at)}</TableCell>
                                                    <TableCell>{formatDate(invite.expires_at)}</TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex justify-end gap-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleResendInvite(invite.id)}
                                                                disabled={resendingId === invite.id}
                                                                data-testid={`resend-invite-${invite.id}`}
                                                                title="Einladung erneut senden"
                                                            >
                                                                {resendingId === invite.id ? (
                                                                    <Loader2 size={16} className="animate-spin" />
                                                                ) : (
                                                                    <RefreshCw size={16} />
                                                                )}
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                                                onClick={() => setDeleteInviteId(invite.id)}
                                                                data-testid={`delete-invite-${invite.id}`}
                                                            >
                                                                <Trash2 size={16} />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Delete Sales Rep Confirmation */}
            <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Vertriebler löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Der Vertriebler wird gelöscht. Seine Kunden und Empfehlungen bleiben erhalten, 
                            werden aber keinem Vertriebler mehr zugeordnet.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-red-500 hover:bg-red-600"
                        >
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete Invitation Confirmation */}
            <AlertDialog open={!!deleteInviteId} onOpenChange={() => setDeleteInviteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Einladung löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Die Einladung wird gelöscht und der Einladungslink wird ungültig.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteInvite}
                            className="bg-red-500 hover:bg-red-600"
                        >
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </DashboardLayout>
    );
};
